import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useAuth } from "../Login/AuthContext";

const ALERT_COLOR = "#92400e";

const RegistroProducto = () => {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [precio, setPrecio] = useState("");
  const [stock, setStock] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [categorias, setCategorias] = useState([]);
  const [imagen, setImagen] = useState(null);
  const [preview, setPreview] = useState("");

  const API_URL = process.env.REACT_APP_API_URL;
  const { token } = useAuth();

  useEffect(() => {
    const fetchCategorias = async () => {
      try {
        const response = await axios.get(`${API_URL}/categorias`);
        setCategorias(response.data);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar las categorias.",
          confirmButtonColor: ALERT_COLOR,
        });
      }
    };
    fetchCategorias();
  }, [API_URL]);

  const mostrarError = (text) => {
    Swal.fire({
      icon: "error",
      title: "Error",
      text,
      confirmButtonColor: ALERT_COLOR,
    });
  };

  const validarCampos = () => {
    if (!nombre.trim()) {
      mostrarError("El nombre del producto es obligatorio.");
      return false;
    }
    if (nombre.length > 100) {
      mostrarError("El nombre no puede tener mas de 100 caracteres.");
      return false;
    }
    if (!precio || isNaN(precio) || Number(precio) < 0) {
      mostrarError("El precio debe ser un numero valido mayor o igual a 0.");
      return false;
    }
    if (!stock || isNaN(stock) || !Number.isInteger(Number(stock)) || Number(stock) < 0) {
      mostrarError("El stock debe ser un numero entero valido mayor o igual a 0.");
      return false;
    }
    if (!categoriaId) {
      mostrarError("Debe seleccionar una categoria.");
      return false;
    }
    if (!imagen) {
      mostrarError("Debe seleccionar una imagen del producto.");
      return false;
    }
    return true;
  };

  const subirImagen = async () => {
    if (!imagen) return null;

    const formData = new FormData();
    formData.append("imagen", imagen);

    const response = await axios.post(
      `${API_URL}/upload/producto-imagen`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.imagen_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarCampos()) return;

    try {
      let imagenUrl = null;
      if (imagen) {
        imagenUrl = await subirImagen();
      }

      const datosFormateados = {
        nombre: nombre.trim(),
        descripcion: descripcion.trim(),
        precio: Number(precio),
        stock: Number(stock),
        categoria_id: Number(categoriaId),
        ...(imagenUrl && { imagen_url: imagenUrl }),
      };

      await axios.post(`${API_URL}/productos`, datosFormateados, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      Swal.fire({
        title: "Producto registrado",
        text: "El producto se ha registrado correctamente.",
        icon: "success",
        confirmButtonColor: ALERT_COLOR,
      });

      limpiarFormulario();
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.msg || "";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg || "No se pudo registrar el producto. Intente nuevamente.",
        confirmButtonColor: ALERT_COLOR,
      });
    }
  };

  const limpiarFormulario = () => {
    setNombre("");
    setDescripcion("");
    setPrecio("");
    setStock("");
    setCategoriaId("");
    setImagen(null);
    setPreview("");
  };

  const handleImagenChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImagen(file);
    setPreview(URL.createObjectURL(file));
  };

  return (
    <section className="min-h-screen flex flex-col items-center justify-center p-4 md:p-6 bg-stone-50">
      <div className="p-5 md:p-8 bg-white shadow-xl rounded-lg w-full max-w-2xl border border-stone-200">
        <div className="text-center mb-6">
          <span className="text-sm font-semibold uppercase tracking-wide text-amber-700">
            Catalogo de arte
          </span>
          <h2 className="mt-2 text-2xl md:text-3xl font-extrabold text-stone-900">
            Registrar Producto
          </h2>
          <p className="text-stone-600 mt-2">
            Complete el formulario para sumar una nueva pieza a la tienda.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
          <Input label="Nombre del producto" value={nombre} onChange={setNombre} required />
          <Textarea label="Descripcion" value={descripcion} onChange={setDescripcion} />
          <Input label="Precio" value={precio} onChange={setPrecio} type="number" required />
          <Input label="Stock" value={stock} onChange={setStock} type="number" required />
          <SelectCategoria
            label="Categoria"
            value={categoriaId}
            onChange={setCategoriaId}
            categorias={categorias}
            required
          />

          <div className="flex flex-col">
            <label className="mb-2 text-stone-800 font-medium">
              Imagen del producto
            </label>
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/webp"
              onChange={handleImagenChange}
              required
              className="border border-stone-300 bg-white p-2 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-600"
            />
          </div>

          {preview && (
            <div className="flex justify-center mt-2">
              <img
                src={preview}
                alt="Vista previa"
                className="w-40 h-40 object-cover rounded-lg shadow border border-stone-200"
              />
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between mt-6 gap-3 sm:gap-4">
            <button
              type="button"
              onClick={limpiarFormulario}
              className="w-full sm:w-1/2 p-3 bg-stone-100 text-stone-800 font-semibold hover:bg-stone-200 transition rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="w-full sm:w-1/2 p-3 bg-stone-900 text-white font-semibold hover:bg-amber-800 transition rounded-lg shadow-md"
            >
              Registrar
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const fieldClassName =
  "border border-stone-300 bg-white p-3 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-600";

const Input = ({ label, value, onChange, type = "text", required = false }) => (
  <div className="flex flex-col">
    <label className="mb-2 text-stone-800 font-medium">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={fieldClassName}
    />
  </div>
);

const Textarea = ({ label, value, onChange }) => (
  <div className="flex flex-col">
    <label className="mb-2 text-stone-800 font-medium">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows="4"
      className={fieldClassName}
    />
  </div>
);

const SelectCategoria = ({ label, value, onChange, categorias, required = false }) => (
  <div className="flex flex-col">
    <label className="mb-2 text-stone-800 font-medium">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={fieldClassName}
    >
      <option value="">Seleccione una categoria</option>
      {categorias.map((categoria) => (
        <option key={categoria.id} value={categoria.id}>
          {categoria.nombre}
        </option>
      ))}
    </select>
  </div>
);

export default RegistroProducto;
